import { jwtDecode } from 'jwt-decode';
import { getUserIdFromToken } from '@/hooks/useAuthValidation';

export const isAuthenticated = () => {
  if (typeof window === 'undefined') return false;
  const accessToken = localStorage.getItem('accessToken');
  return !!accessToken;
};

/**
 * Per-account user id from the CURRENT access token (its `sub` claim — the
 * same id the backend resolves to `req.user.userId`). Use this to scope
 * account-sensitive caches (TCX-699): the token is written synchronously at
 * login, whereas Redux/profile user state refreshes async, so during a
 * same-session account swap only the token is guaranteed to identify the
 * account the requests are actually authorized as.
 */
export const getAuthUserId = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  const accessToken = localStorage.getItem('accessToken');
  if (!accessToken) return undefined;
  try {
    return jwtDecode<{ sub?: string }>(accessToken).sub || undefined;
  } catch {
    return undefined;
  }
};

// TCX-790: React Query keys for user-owned data must be scoped by the
// token-derived user id so cached entries can never bleed across a same-tab
// account swap. Every source `useQuery`/`useInfiniteQuery` for the same
// logical resource must build its key from these helpers so the ids stay
// identical (e.g. the shared `['profile', userId]` key that withAuth,
// book-a-call and my-profile all rely on for request dedup). The id comes
// from the access token, never Redux/profile mirrors (TCX-699/TCX-632).
// invalidateQueries with the bare prefix (e.g. `['profile']`) still matches
// these scoped keys because v5 does prefix matching by default.
export const profileQueryKey = () => ['profile', getUserIdFromToken()] as const;
export const chatsQueryKey = () => ['chats', getUserIdFromToken()] as const;
export const paymentMethodsQueryKey = () =>
  ['getPaymentMethods', getUserIdFromToken()] as const;
export const notificationsQueryKey = (page: number) =>
  ['notifications', getUserIdFromToken(), page] as const;
export const latestSessionQueryKey = () =>
  ['latest-session', getUserIdFromToken()] as const;
export const consultantFeedbackRequestQueryKey = () =>
  ['consultant-feedback-request', getUserIdFromToken()] as const;
export const bookingsQueryKey = () =>
  ['bookings', getUserIdFromToken()] as const;
export const paymentsQueryKey = () =>
  ['payments', getUserIdFromToken()] as const;
export const paymentsTotalQueryKey = () =>
  ['payments-total', getUserIdFromToken()] as const;
export const paymentsChartQueryKey = (range: 'week' | 'year') =>
  ['payments-chart', getUserIdFromToken(), range] as const;
export const pendingPaymentsQueryKey = () =>
  ['pending-payments', getUserIdFromToken()] as const;
export const calendarsQueryKey = () =>
  ['calendars', getUserIdFromToken()] as const;
export const availabilityQueryKey = () =>
  ['my-availability', getUserIdFromToken()] as const;

// TCX-1070: the consultant's own acceptance state per brand — account-scoped
// (it says whether THIS consultant accepted), so keyed by the token id.
export const consultantReferralTermsQueryKey = (franchiseId: string) =>
  ['consultantReferralTerms', getUserIdFromToken(), franchiseId] as const;

// Individual chat contents. The chat list is scoped via `chatsQueryKey`; the
// per-chat detail must be scoped too so a same-tab account swap can't surface
// the previous account's conversation for a re-used chat id.
export const chatDetailQueryKey = (chatId: string | undefined) =>
  ['chat', getUserIdFromToken(), chatId] as const;

// Booking detail (the franchisee/consultant "Booking Details" page). We use a
// dedicated `'booking'` prefix (singular) rather than extending the
// `['bookings', userId]` LIST key so the detail can never prefix-collide with
// the list. NOTE: this is distinct from the ADMIN booking detail
// (`['booking', id]`) — that view is cross-user and stays unscoped; the extra
// userId slot here keeps the two from ever matching each other.
export const bookingDetailQueryKey = (bookingId: string | undefined) =>
  ['booking', getUserIdFromToken(), bookingId] as const;

// Private notes a franchisor keeps about a consultant (TCX-833). This is
// franchise-account-scoped data, so the key must carry the token-derived user
// id like every other identity-scoped query above — otherwise a same-tab
// account swap could surface a previous account's cached notes for the same
// consultant id.
// Prefix covering EVERY cached page of one consultant's notes — use this to
// invalidate after a save, so the collapsed page and the expanded history are
// both refreshed rather than only whichever is on screen.
export const consultantNotesQueryKey = (consultantId: string | undefined) =>
  ['consultant-notes', getUserIdFromToken(), consultantId] as const;

// A single page of that history. The page size is part of the key so the
// collapsed view (first N) and the expanded view ('all') are cached side by
// side instead of overwriting each other, and both prefix-match the key above.
export const consultantNotesPageQueryKey = (
  consultantId: string | undefined,
  page: number | 'all',
) => [...consultantNotesQueryKey(consultantId), page] as const;

// Consultant PROFILE detail as seen by a franchisor. Since TCX-833 this
// response carries viewer-private relationship data (`meetingsCount` /
// `lastMeetingAt`, scoped to the caller's franchise account), so a same-tab
// account swap must not be able to serve the previous franchisor's numbers for
// the same consultant id. Scoping also stops this key from colliding with the
// admin consultant-detail views, which use the same `['consultant', id]` shape
// with different fetchers and stay unscoped (cross-user admin reads).
export const consultantDetailQueryKey = (consultantId: string | undefined) =>
  ['consultant', getUserIdFromToken(), consultantId] as const;

// Find-Consultant / dashboard "featured consultants" lists. The response
// embeds the CURRENT user's favouriteConsultants + totalFavorites, so the key
// must be scoped by the token id (TCX-699 class). Extra params (filters,
// active tab, group filter) follow the userId slot. The bare `['consultants']`
// invalidations from the favourite toggle / FreeTrial still prefix-match these
// scoped keys. Admin consultant tables use their own keys and are out of scope.
export const consultantsListQueryKey = (
  ...params: readonly (string | number)[]
) => ['consultants', getUserIdFromToken(), ...params] as const;

// Find-Franchisor list. Same reasoning as `consultantsListQueryKey` — the
// response carries the current user's favouriteFranchisees + totalFavorites.
// Admin franchisor tables use their own `['franchisors', search, page, limit]`
// keys and are intentionally left unscoped (cross-user admin views).
export const franchisorsListQueryKey = (
  ...params: readonly (string | number)[]
) => ['franchisors', getUserIdFromToken(), ...params] as const;

// Activation flows (consultant + franchisor). These are PRE-AUTH: there is no
// access token yet, so the key must be scoped by the URL invite `token`, NOT
// getUserIdFromToken(). Without the token in the key, a constant
// `['activationData']` key would cache-collide across invites, so opening a
// second, different invite link in the same tab could surface the first
// invite's cached data — showing/using the wrong invite (TCX-790). Scoping by
// token keeps each invite on its own cache entry regardless of staleTime.
export const activationDataQueryKey = (token: string | null) =>
  ['activationData', token] as const;

export const isPublicRoute = () => {
  if (typeof window === 'undefined') return false;
  const publicRoutePatterns = [
    /^\/consultants\/[^\/]+$/,
    /^\/franchisors\/[^\/]+$/,
  ];

  return publicRoutePatterns.some((pattern) => pattern.test(location.pathname));
};
